"use client";

import { PageSection, SURFACE } from "@commerce/components/page";
import { clock, Time, useNow, useTimeZone } from "@commerce/components/time";
import { orpc } from "@shared/lib/orpc";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import { LinkButton } from "@ui/components/link-button";
import { Skeleton } from "@ui/components/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/components/tabs";
import { cn } from "@ui/lib";
import {
	ArrowLeftIcon,
	CircleAlertIcon,
	EllipsisIcon,
	ExternalLinkIcon,
	MapPinIcon,
	MessagesSquareIcon,
	PackageIcon,
	TriangleAlertIcon,
	UserIcon,
} from "lucide-react";
import { useState } from "react";
import { LineEditor } from "./LineEditor";
import {
	ACTOR_LABEL,
	EVENT_LABEL,
	isFinal,
	keyedLines,
	mainAction,
	mapsLink,
	money,
	orderName,
	PAYMENT_LABEL,
	PAYMENT_STATUS_LABEL,
	STATUS_META,
	SURFACE_LABEL,
	secondaryActions,
} from "./meta";
import {
	AcceptDialog,
	ConfirmAddressDialog,
	NoteDialog,
	SendDialog,
	StatusDialog,
	StopOrderDialog,
} from "./OrderActions";
import type { OrderDetail as Detail, OrderEvent } from "./types";

type Dialog =
	| "main"
	| "reject"
	| "cancel"
	| "note"
	| "lines"
	| "payment_link"
	| "resend"
	| null;

/** A new order waiting longer than this shows its wait in the warning colour. */
const WAIT_WARN_MS = 5 * 60_000;

/**
 * One order: who, where, when, what, and the one action that moves it on. On phones the
 * action sits in a sticky bar at the bottom so it never scrolls away.
 */
export function OrderDetail({
	order,
	organizationId,
	organizationSlug,
	isAdmin,
	onBack,
}: {
	order: Detail;
	organizationId: string;
	organizationSlug: string;
	isAdmin: boolean;
	onBack: () => void;
}) {
	const [dialog, setDialog] = useState<Dialog>(null);
	const action = mainAction(order);
	const more = secondaryActions(order);
	const status = STATUS_META[order.status];

	const mainButton = action ? (
		<Button onClick={() => setDialog("main")} className="max-lg:flex-1">
			{action.kind === "confirm_address" ? (
				<MapPinIcon data-icon="inline-start" />
			) : null}
			{action.label}
		</Button>
	) : null;
	const menu = <MoreActions order={order} more={more} onPick={setDialog} />;

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<header className="flex shrink-0 items-start gap-2 border-border/70 border-b px-4 py-3 md:px-6">
				<Button
					variant="ghost"
					size="icon-sm"
					onClick={onBack}
					aria-label="Back to orders"
					className="-ml-1.5 mt-0.5 lg:hidden"
				>
					<ArrowLeftIcon />
				</Button>
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<h2 className="flex flex-wrap items-center gap-2 font-semibold text-lg tracking-tight">
						Order {orderName(order)}
						<Badge variant={status.variant}>
							{order.statusLabel ?? status.label}
						</Badge>
					</h2>
					<OrderByline order={order} />
				</div>
				<div className="flex shrink-0 items-center gap-2 max-lg:hidden">
					{mainButton}
					{menu}
				</div>
				<div className="shrink-0 lg:hidden">{menu}</div>
			</header>

			<div className="min-h-0 flex-1 overflow-y-auto">
				<div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-5 md:px-6">
					<OrderNotices order={order} />
					<Delivery order={order} />
					<Items
						order={order}
						onEdit={
							more.editLines ? () => setDialog("lines") : null
						}
					/>
					<Tabs defaultValue="timeline" className="gap-4">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<TabsList>
								<TabsTrigger value="timeline">
									Timeline
								</TabsTrigger>
								<TabsTrigger value="technical">
									Technical
								</TabsTrigger>
							</TabsList>
							<OrderLinks
								order={order}
								organizationSlug={organizationSlug}
							/>
						</div>
						<TabsContent value="timeline">
							<Timeline events={order.events} />
						</TabsContent>
						<TabsContent value="technical">
							<Technical
								order={order}
								organizationId={organizationId}
								organizationSlug={organizationSlug}
								isAdmin={isAdmin}
							/>
						</TabsContent>
					</Tabs>
				</div>
			</div>

			{mainButton ? (
				<div className="sticky bottom-0 flex shrink-0 gap-2 border-border/70 border-t bg-background/95 px-4 py-3 lg:hidden">
					{mainButton}
				</div>
			) : null}

			<OrderDialogs
				order={order}
				organizationId={organizationId}
				action={action}
				reject={more.reject}
				dialog={dialog}
				onClose={() => setDialog(null)}
			/>
		</div>
	);
}

/** "waiting 4 min · Hamra branch · from WhatsApp" under the order's name. */
function OrderByline({ order }: { order: Detail }) {
	return (
		<p className="flex flex-wrap items-center gap-x-3 text-muted-foreground text-sm sm:gap-x-1.5">
			<WaitingFor order={order} />
			{order.store ? (
				<span>
					<span aria-hidden className="max-sm:hidden">
						·{" "}
					</span>
					{order.store.name}
				</span>
			) : null}
			{order.surface ? (
				<span>
					<span aria-hidden className="max-sm:hidden">
						·{" "}
					</span>
					from {SURFACE_LABEL[order.surface] ?? order.surface}
				</span>
			) : null}
		</p>
	);
}

function MoreActions({
	order,
	more,
	onPick,
}: {
	order: Detail;
	more: ReturnType<typeof secondaryActions>;
	onPick: (dialog: Dialog) => void;
}) {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				render={
					<Button
						variant="outline"
						size="icon"
						aria-label={`More actions for order ${orderName(order)}`}
					>
						<EllipsisIcon />
					</Button>
				}
			/>
			<DropdownMenuContent align="end" className="w-56">
				<DropdownMenuGroup>
					{more.editLines ? (
						<DropdownMenuItem onClick={() => onPick("lines")}>
							Edit items
						</DropdownMenuItem>
					) : null}
					{more.paymentLink ? (
						<DropdownMenuItem
							onClick={() => onPick("payment_link")}
						>
							Send a payment link
						</DropdownMenuItem>
					) : null}
					{more.resend ? (
						<DropdownMenuItem onClick={() => onPick("resend")}>
							Send the last update again
						</DropdownMenuItem>
					) : null}
					<DropdownMenuItem onClick={() => onPick("note")}>
						Add a note
					</DropdownMenuItem>
				</DropdownMenuGroup>
				{more.reject || more.cancel ? (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							variant="destructive"
							onClick={() =>
								onPick(more.reject ? "reject" : "cancel")
							}
						>
							{more.reject ? "Reject order" : "Cancel order"}
						</DropdownMenuItem>
					</>
				) : null}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

/** What staff must know before acting: a missing delivery point, why it was cancelled. */
function OrderNotices({ order }: { order: Detail }) {
	return (
		<>
			{order.addressNeedsConfirmation &&
			order.fulfilment !== "PICKUP" &&
			!isFinal(order.status) ? (
				<div
					role="status"
					className="flex items-start gap-3 rounded-2xl bg-warning/14 px-4 py-3 text-sm dark:bg-warning/10"
				>
					<TriangleAlertIcon
						className="mt-0.5 size-4 shrink-0 text-warning-foreground dark:text-warning"
						aria-hidden
					/>
					<div className="flex flex-col gap-0.5">
						<span className="font-medium">
							No delivery point yet
						</span>
						<span className="text-muted-foreground">
							The shopper typed an area. Set the point before you
							accept, so the driver finds them.
						</span>
					</div>
				</div>
			) : null}
			{order.status === "CANCELLED" && order.cancelReason ? (
				<div className="flex items-start gap-3 rounded-2xl bg-destructive/8 px-4 py-3 text-sm">
					<CircleAlertIcon
						className="mt-0.5 size-4 shrink-0 text-destructive"
						aria-hidden
					/>
					<span>
						Cancelled
						{order.cancelledBy
							? ` by ${ACTOR_LABEL[order.cancelledBy === "retailer" ? "staff" : order.cancelledBy]?.toLowerCase() ?? order.cancelledBy}`
							: ""}
						: <span dir="auto">{order.cancelReason}</span>
					</span>
				</div>
			) : null}
		</>
	);
}

function OrderLinks({
	order,
	organizationSlug,
}: {
	order: Detail;
	organizationSlug: string;
}) {
	return (
		<div className="flex gap-2">
			{order.conversationId ? (
				<LinkButton
					variant="outline"
					size="sm"
					to="/app/$organizationSlug/conversations"
					params={{ organizationSlug }}
					search={{ id: order.conversationId }}
				>
					<MessagesSquareIcon data-icon="inline-start" />
					Conversation
				</LinkButton>
			) : null}
			{order.customer ? (
				<LinkButton
					variant="outline"
					size="sm"
					to="/app/$organizationSlug/customers/$customerId"
					params={{
						organizationSlug,
						customerId: order.customer.id,
					}}
				>
					<UserIcon data-icon="inline-start" />
					Customer
				</LinkButton>
			) : null}
		</div>
	);
}

/** Every dialog an order's actions open; `dialog` says which one is open. */
function OrderDialogs({
	order,
	organizationId,
	action,
	reject,
	dialog,
	onClose,
}: {
	order: Detail;
	organizationId: string;
	action: ReturnType<typeof mainAction>;
	reject: boolean;
	dialog: Dialog;
	onClose: () => void;
}) {
	const close = (open: boolean) => {
		if (!open) {
			onClose();
		}
	};
	return (
		<>
			{action?.kind === "accept" ? (
				<AcceptDialog
					order={order}
					organizationId={organizationId}
					open={dialog === "main"}
					onOpenChange={close}
				/>
			) : null}
			{action?.kind === "confirm_address" ? (
				<ConfirmAddressDialog
					order={order}
					organizationId={organizationId}
					open={dialog === "main"}
					onOpenChange={close}
				/>
			) : null}
			{action?.kind === "status" ? (
				<StatusDialog
					order={order}
					organizationId={organizationId}
					status={action.status}
					label={action.label}
					done={action.done}
					open={dialog === "main"}
					onOpenChange={close}
				/>
			) : null}
			<StopOrderDialog
				order={order}
				organizationId={organizationId}
				kind={reject ? "reject" : "cancel"}
				open={dialog === "reject" || dialog === "cancel"}
				onOpenChange={close}
			/>
			<NoteDialog
				order={order}
				organizationId={organizationId}
				open={dialog === "note"}
				onOpenChange={close}
			/>
			<SendDialog
				order={order}
				organizationId={organizationId}
				kind={dialog === "resend" ? "resend" : "payment_link"}
				open={dialog === "payment_link" || dialog === "resend"}
				onOpenChange={close}
			/>
			<LineEditor
				order={order}
				organizationId={organizationId}
				open={dialog === "lines"}
				onOpenChange={close}
			/>
		</>
	);
}

/** "waiting 4 min" on a new order, from when it was placed; the placed time otherwise. */
export function WaitingFor({
	order,
	className,
}: {
	order: Pick<Detail, "status" | "placedAt">;
	className?: string;
}) {
	const now = useNow();
	const placed = new Date(order.placedAt);
	if (order.status !== "PLACED" || now === 0) {
		return (
			<span className={className}>
				Placed <Time at={placed} label="Placed" />
			</span>
		);
	}
	const waited = Math.max(0, now - placed.getTime());
	const minutes = Math.floor(waited / 60_000);
	return (
		<span
			className={cn(
				"tabular",
				waited > WAIT_WARN_MS &&
					"font-medium text-warning-foreground dark:text-warning",
				className,
			)}
		>
			waiting{" "}
			{minutes < 1
				? "under a minute"
				: minutes < 60
					? `${minutes} min`
					: `${Math.floor(minutes / 60)} h ${minutes % 60} min`}
		</span>
	);
}

function Delivery({ order }: { order: Detail }) {
	return (
		<dl
			className={cn(
				SURFACE,
				"grid gap-x-6 gap-y-3 p-4 text-sm sm:grid-cols-[auto_1fr] md:p-5",
			)}
		>
			<dt className="text-muted-foreground">Customer</dt>
			<dd className="flex flex-wrap items-center gap-x-2">
				<span dir="auto" className="font-medium">
					{order.customer?.name ?? "Shopper"}
				</span>
				{order.customer?.phone ? (
					<span className="font-mono text-muted-foreground text-xs">
						{order.customer.phone}
					</span>
				) : null}
			</dd>

			<dt className="text-muted-foreground">
				{order.fulfilment === "PICKUP" ? "Pickup" : "Deliver to"}
			</dt>
			<dd className="flex flex-col gap-1">
				{order.fulfilment === "PICKUP" ? (
					<span>{order.store?.name ?? "In store"}</span>
				) : (
					<DeliveryAddress address={order.deliveryAddress} />
				)}
			</dd>

			<dt className="text-muted-foreground">When</dt>
			<dd className="tabular">
				<SlotTime slotStart={order.slotStart} slotEnd={order.slotEnd} />
			</dd>

			{order.etaAt ? (
				<>
					<dt className="text-muted-foreground">Ready by</dt>
					<dd className="tabular">
						<Time at={new Date(order.etaAt)} label="Ready by" />
					</dd>
				</>
			) : null}

			{order.paymentMethod ? (
				<>
					<dt className="text-muted-foreground">Payment</dt>
					<dd className="flex flex-wrap items-center gap-2">
						<Payment
							method={order.paymentMethod}
							status={order.paymentStatus}
						/>
					</dd>
				</>
			) : null}

			{order.substitution ? (
				<>
					<dt className="text-muted-foreground">If out of stock</dt>
					<dd>
						{order.substitution === "SIMILAR"
							? "Replace with something similar"
							: "Leave it out"}
					</dd>
				</>
			) : null}

			{order.notes ? (
				<>
					<dt className="text-muted-foreground">Shopper's note</dt>
					<dd dir="auto" className="text-left">
						{order.notes}
					</dd>
				</>
			) : null}
		</dl>
	);
}

type Address = Detail["deliveryAddress"];

/** "Home, Hamra, Bldg 4, floor 2, apt 5", or null when nothing was given. */
function placeOf(address: Address): string | null {
	if (!address) {
		return null;
	}
	return (
		[
			address.label,
			address.area,
			address.building,
			address.floor ? `floor ${address.floor}` : null,
			address.apartment ? `apt ${address.apartment}` : null,
		]
			.filter(Boolean)
			.join(", ") || null
	);
}

function DeliveryAddress({ address }: { address: Address }) {
	const place = placeOf(address);
	if (!address || !place) {
		return <span className="text-muted-foreground">No address</span>;
	}
	const { latitude, longitude } = address;
	return (
		<>
			<span dir="auto" className="text-left">
				{place}
			</span>
			{address.landmark ? (
				<span dir="auto" className="text-left text-muted-foreground">
					Landmark: {address.landmark}
				</span>
			) : null}
			{address.notes ? (
				<span dir="auto" className="text-left text-muted-foreground">
					{address.notes}
				</span>
			) : null}
			{latitude !== null && longitude !== null ? (
				<a
					href={mapsLink(latitude, longitude)}
					target="_blank"
					rel="noreferrer"
					className="inline-flex w-fit items-center gap-1 underline underline-offset-4"
				>
					<MapPinIcon className="size-3.5" aria-hidden />
					Open map
					<ExternalLinkIcon className="size-3" aria-hidden />
				</a>
			) : null}
		</>
	);
}

function SlotTime({
	slotStart,
	slotEnd,
}: {
	slotStart: Detail["slotStart"];
	slotEnd: Detail["slotEnd"];
}) {
	const timeZone = useTimeZone();
	if (!slotStart) {
		return "As soon as possible";
	}
	return (
		<>
			<Time at={new Date(slotStart)} label="Slot" />
			{slotEnd ? ` to ${clock(new Date(slotEnd), timeZone)}` : null}
		</>
	);
}

const PAYMENT_STATUS_VARIANT: Partial<
	Record<Detail["paymentStatus"], "success" | "destructive">
> = {
	PAID: "success",
	FAILED: "destructive",
};

function Payment({
	method,
	status,
}: {
	method: string;
	status: Detail["paymentStatus"];
}) {
	const state = PAYMENT_STATUS_LABEL[status];
	return (
		<>
			{PAYMENT_LABEL[method] ?? method}
			{state ? (
				<Badge variant={PAYMENT_STATUS_VARIANT[status] ?? "secondary"}>
					{state}
				</Badge>
			) : null}
		</>
	);
}

function Items({
	order,
	onEdit,
}: {
	order: Detail;
	onEdit: (() => void) | null;
}) {
	const totals = [
		{ label: "Subtotal", value: money(order.subtotal, order.currency) },
		{ label: "Delivery", value: money(order.deliveryFee, order.currency) },
		order.discount && Number(order.discount) !== 0
			? {
					label: "Discount",
					value: `−${money(order.discount, order.currency)}`,
				}
			: null,
	].filter((row) => row?.value);
	return (
		<PageSection
			title={`Items (${order.itemCount})`}
			actions={
				onEdit ? (
					<Button variant="ghost" size="sm" onClick={onEdit}>
						Edit
					</Button>
				) : null
			}
		>
			<div className={SURFACE}>
				<ul className="divide-y divide-border/70">
					{keyedLines(order.lines).map(({ key, line }) => (
						<li
							key={key}
							className="flex items-center gap-3 px-4 py-2.5 md:px-5"
						>
							{line.imageUrl ? (
								<img
									src={line.imageUrl}
									alt=""
									loading="lazy"
									className="size-9 shrink-0 rounded-xl bg-muted object-contain"
								/>
							) : (
								<span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted">
									<PackageIcon
										className="size-4 text-muted-foreground"
										aria-hidden
									/>
								</span>
							)}
							<span className="w-8 shrink-0 text-muted-foreground text-sm tabular">
								{line.quantity} ×
							</span>
							<span className="flex min-w-0 flex-1 flex-col">
								<span
									dir="auto"
									className={cn(
										"truncate text-left text-sm",
										line.lineStatus === "unavailable" &&
											"text-muted-foreground line-through",
									)}
								>
									{line.title}
								</span>
								{line.lineStatus && line.lineStatus !== "ok" ? (
									<span className="text-muted-foreground text-xs">
										{line.lineStatus === "unavailable"
											? "Not available"
											: line.lineStatus === "substituted"
												? "Replaced"
												: line.originalQuantity !== null
													? `Changed from ${line.originalQuantity}`
													: "Changed"}
									</span>
								) : null}
							</span>
							{line.lineStatus === "unavailable" ? (
								<TriangleAlertIcon
									className="size-4 shrink-0 text-warning-foreground dark:text-warning"
									aria-label="Not available"
								/>
							) : null}
							<span className="shrink-0 text-sm tabular">
								{money(
									String(
										Number(line.unitPrice) * line.quantity,
									),
									order.currency,
								)}
							</span>
						</li>
					))}
				</ul>
				<dl className="flex flex-col gap-1 border-border/70 border-t bg-muted/40 px-4 py-3 text-sm md:px-5">
					{totals.map((row) =>
						row ? (
							<div
								key={row.label}
								className="flex justify-between gap-4"
							>
								<dt className="text-muted-foreground">
									{row.label}
								</dt>
								<dd className="tabular">{row.value}</dd>
							</div>
						) : null,
					)}
					<div className="flex justify-between gap-4 font-semibold">
						<dt>Total</dt>
						<dd className="tabular">
							{money(order.total, order.currency)}
						</dd>
					</div>
				</dl>
			</div>
		</PageSection>
	);
}

function Timeline({ events }: { events: OrderEvent[] }) {
	if (events.length === 0) {
		return (
			<p className="text-muted-foreground text-sm">
				Nothing happened yet.
			</p>
		);
	}
	return (
		<ol className="flex flex-col">
			{events.map((event, index) => (
				<li
					key={event.id}
					className="relative flex gap-3 pb-4 last:pb-0"
				>
					{index < events.length - 1 ? (
						<span
							aria-hidden
							className="absolute top-4 bottom-0 left-[3.5px] w-px bg-border"
						/>
					) : null}
					<span
						aria-hidden
						className={cn(
							"mt-1.5 size-2 shrink-0 rounded-full",
							event.type === "message_failed" ||
								event.type === "adapter_error"
								? "bg-destructive"
								: "bg-muted-foreground/50",
						)}
					/>
					<div className="flex min-w-0 flex-1 flex-col gap-0.5 text-sm">
						<div className="flex flex-wrap items-baseline gap-x-2">
							<span className="font-medium">
								{EVENT_LABEL[event.type] ?? event.type}
							</span>
							<span className="text-muted-foreground text-xs">
								{event.actorName ??
									ACTOR_LABEL[event.actorKind] ??
									event.actorKind}
								{" · "}
								<Time at={new Date(event.createdAt)} />
							</span>
						</div>
						<EventDetail event={event} />
					</div>
				</li>
			))}
		</ol>
	);
}

/** The few fields of an event's data worth a line: a note, a reason, a status. */
function eventText(data: Record<string, unknown>): string | null {
	for (const field of ["note", "reason"]) {
		const value = data[field];
		if (typeof value === "string") {
			return value;
		}
	}
	const status = data["status"];
	if (typeof status === "string" && status in STATUS_META) {
		return `Now ${STATUS_META[status as keyof typeof STATUS_META].label.toLowerCase()}`;
	}
	return typeof data["error"] === "string" ? data["error"] : null;
}

function EventDetail({ event }: { event: OrderEvent }) {
	const text = eventText((event.data ?? {}) as Record<string, unknown>);
	const message = event.shopperMessage;
	return (
		<>
			{text ? (
				<p dir="auto" className="text-left text-muted-foreground">
					{text}
				</p>
			) : null}
			{message?.text ? (
				<p
					dir="auto"
					className="mt-1 whitespace-pre-line rounded-xl bg-muted/60 px-3 py-2 text-left text-sm"
				>
					{message.text}
					<span className="mt-1 flex items-center gap-1.5 text-muted-foreground text-xs">
						{message.templateName ? (
							<span className="font-mono">
								{message.templateName}
							</span>
						) : null}
						{message.status ? (
							<span>· {message.status}</span>
						) : null}
					</span>
				</p>
			) : null}
		</>
	);
}

/** Every exchange with Meta and the order system about this order (ChannelLog by order id). */
function Technical({
	order,
	organizationId,
	organizationSlug,
	isAdmin,
}: {
	order: Detail;
	organizationId: string;
	organizationSlug: string;
	isAdmin: boolean;
}) {
	const logs = useQuery({
		...orpc.logs.list.queryOptions({
			input: { organizationId, q: order.id, limit: 50 },
		}),
		enabled: isAdmin,
	});
	const ids = (
		<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
			<dt className="text-muted-foreground">Order id</dt>
			<dd className="truncate font-mono text-xs leading-5">{order.id}</dd>
			<dt className="text-muted-foreground">Retailer's id</dt>
			<dd className="truncate font-mono text-xs leading-5">
				{order.externalOrderId}
			</dd>
			{order.backend ? (
				<>
					<dt className="text-muted-foreground">Order system</dt>
					<dd className="font-mono text-xs leading-5">
						{order.backend}
					</dd>
				</>
			) : null}
			{order.checkoutId ? (
				<>
					<dt className="text-muted-foreground">Checkout</dt>
					<dd className="truncate font-mono text-xs leading-5">
						{order.checkoutId}
					</dd>
				</>
			) : null}
		</dl>
	);
	if (!isAdmin) {
		return (
			<div className="flex flex-col gap-4">
				{ids}
				<p className="text-muted-foreground text-sm">
					Messages and order system calls are in Logs, which
					organization admins can open.
				</p>
			</div>
		);
	}
	return (
		<div className="flex flex-col gap-4">
			{ids}
			{logs.isPending ? (
				<div className="flex flex-col gap-2">
					<Skeleton className="h-9 w-full" />
					<Skeleton className="h-9 w-full" />
				</div>
			) : logs.isError ? (
				<p className="text-muted-foreground text-sm">
					Couldn't load the logs for this order.
				</p>
			) : logs.data.logs.length === 0 ? (
				<Empty className="rounded-3xl border py-8">
					<EmptyHeader>
						<EmptyMedia variant="icon">
							<CircleAlertIcon />
						</EmptyMedia>
						<EmptyTitle>No logs for this order</EmptyTitle>
						<EmptyDescription>
							Messages to the shopper and calls to your order
							system show here.
						</EmptyDescription>
					</EmptyHeader>
				</Empty>
			) : (
				<ul className={cn(SURFACE, "divide-y divide-border/70")}>
					{logs.data.logs.map((log) => (
						<li key={log.id}>
							{/* Logs is its own screen (B5a); a plain link keeps this one independent of its route. */}
							<a
								href={`/app/${organizationSlug}/logs?id=${encodeURIComponent(log.id)}`}
								className="flex items-center gap-3 px-4 py-2.5 text-sm outline-none hover:bg-muted/50 focus-visible:bg-muted/50"
							>
								<Badge
									variant={
										log.outcome === "FAILED"
											? "destructive"
											: "secondary"
									}
								>
									{log.outcome === "FAILED" ? "Failed" : "OK"}
								</Badge>
								<span className="min-w-0 flex-1 truncate font-mono text-xs">
									{log.type}
								</span>
								{log.httpStatus ? (
									<span className="text-muted-foreground text-xs tabular">
										{log.httpStatus}
									</span>
								) : null}
								<Time
									at={new Date(log.createdAt)}
									className="shrink-0 text-muted-foreground text-xs tabular"
								/>
							</a>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

export function OrderDetailSkeleton() {
	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="flex items-center gap-3 border-border/70 border-b px-4 py-3 md:px-6">
				<div className="flex flex-1 flex-col gap-2">
					<Skeleton className="h-6 w-44" />
					<Skeleton className="h-4 w-64" />
				</div>
				<Skeleton className="h-8 w-24 max-lg:hidden" />
			</div>
			<div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-5 md:px-6">
				<Skeleton className="h-40 w-full rounded-3xl" />
				<Skeleton className="h-5 w-24" />
				<Skeleton className="h-56 w-full rounded-3xl" />
			</div>
		</div>
	);
}

export function OrderDetailEmpty() {
	return (
		<Empty className="m-auto">
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<PackageIcon />
				</EmptyMedia>
				<EmptyTitle>Pick an order</EmptyTitle>
				<EmptyDescription>
					Its items, address and next step open here.
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	);
}
